import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Plane, Shield, Box } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Government Contracting", path: "/government-contracting" },
    { name: "Certifications", path: "/certifications" },
    { name: "Contact", path: "/contact" },
  ];

  const services = [
    { name: "Aviation Operations", path: "/aviation", icon: Plane },
    { name: "Logistics", path: "/logistics", icon: Box },
    { name: "Aerial Photography", path: "/aerial-photography", icon: Shield },
  ];

  return (
    <footer className="bg-[#1a365d] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/omrsi8tb_1000028432.jpg"
                alt="AMR Global Holdings Logo"
                className="h-16 w-16 object-contain bg-white rounded-sm p-1"
              />
              <div>
                <h3 className="font-heading text-xl">AMR GLOBAL</h3>
                <p className="text-xs text-slate-300 tracking-wider">HOLDINGS LLC</p>
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Veteran-Owned aviation and logistics infrastructure company specializing in 
              rapid deployment support for government and commercial operations.
            </p>
            <div className="flex items-center gap-2 text-[#c5a04a] text-sm font-heading tracking-wider">
              <Shield className="w-4 h-4" />
              VETERAN POWERED. MISSION READY.
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-6 text-[#c5a04a]">QUICK LINKS</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="footer-link text-sm text-slate-300 hover:text-[#c5a04a] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg mb-6 text-[#c5a04a]">SERVICES</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    className="footer-link text-sm text-slate-300 hover:text-[#c5a04a] transition-colors flex items-center gap-2"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <service.icon className="w-4 h-4" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg mb-6 text-[#c5a04a]">CONTACT</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:504-319-7683"
                  className="flex items-start gap-3 text-sm text-slate-300 hover:text-[#c5a04a] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  504-319-7683
                </a>
              </li>
              <li>
                <a
                  href="mailto:AMREID@AMRGLOBALHOLDINGS.COM"
                  className="flex items-start gap-3 text-sm text-slate-300 hover:text-[#c5a04a] transition-colors break-all"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  AMREID@AMRGLOBALHOLDINGS.COM
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-slate-300">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p>6001 Stars and Stripes Blvd</p>
                  <p>Suite 244</p>
                  <p>New Orleans, LA 70126</p>
                  <p className="text-[#c5a04a] mt-1">New Orleans Lakefront Airport (KNEW)</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Certifications Bar */}
      <div className="border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-400 tracking-wider">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#c5a04a] rounded-full"></span>
              VETERAN-OWNED
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#c5a04a] rounded-full"></span>
              SAM.GOV REGISTERED
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#c5a04a] rounded-full"></span>
              HUBZONE ALIGNED
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#c5a04a] rounded-full"></span>
              CAGE CODE ACTIVE
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#c5a04a] rounded-full"></span>
              UEI ACTIVE
            </span>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-slate-700 bg-[#102a43]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-400">
            <p>&copy; {currentYear} AMR Global Holdings LLC. All Rights Reserved.</p>
            <p>Aviation | Logistics | Rapid Deployment</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
